import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import EntityMentionList from '../EntityMentionList';
import { searchEntities, routeForEntity } from '../../../services/entityMentionProviders';

/* Docs v1 — "@" entity tag chips (students · projects · repertoire · tasks).
 *
 * A LIGHTWEIGHT reference, not an embed: the chip stores {entityType, id, label}
 * and links to the entity. It does NOT render live entity data — that's the
 * phase-2 embed card. Storing the label alongside the id is deliberate: a doc
 * must still read sensibly if the entity is later renamed or deleted, and the
 * denormalized mentions[] on the doc is what makes "docs referencing X"
 * answerable without loading every doc's content.
 *
 * Node name is `entityMention` (not `mention`) so it cannot collide with the
 * legacy student-only mention used by Lesson Recap / SongNotes, which stays
 * exactly as it is.
 *
 * SECURITY NOTE: a student chip in a Work doc grants that family nothing. Docs
 * is staff-only permanently (firestore.rules → /workDocs); anything meant to
 * reach a family goes through Lesson Recap or Song Notes.
 */

const EntityMention = Mention.extend({
	name: 'entityMention',

	addAttributes() {
		return {
			id: {
				default: null,
				parseHTML: (el) => el.getAttribute('data-entity-id'),
				renderHTML: (attrs) => (attrs.id ? { 'data-entity-id': attrs.id } : {}),
			},
			label: {
				default: null,
				parseHTML: (el) => el.getAttribute('data-entity-label'),
				renderHTML: (attrs) => (attrs.label ? { 'data-entity-label': attrs.label } : {}),
			},
			entityType: {
				default: null,
				parseHTML: (el) => el.getAttribute('data-entity-type'),
				renderHTML: (attrs) =>
					attrs.entityType ? { 'data-entity-type': attrs.entityType } : {},
			},
		};
	},

	parseHTML() {
		return [{ tag: 'span[data-entity-mention]' }];
	},

	renderHTML({ node, HTMLAttributes }) {
		const hasRoute = !!routeForEntity(node.attrs.entityType, node.attrs.id);
		return [
			'span',
			{
				...HTMLAttributes,
				'data-entity-mention': '',
				'data-no-route': hasRoute ? 'false' : 'true',
				class: 'cyv-mention',
			},
			`@${node.attrs.label || ''}`,
		];
	},

	renderText({ node }) {
		return `@${node.attrs.label || ''}`;
	},
}).configure({
	suggestion: {
		char: '@',

		items: ({ query }) => searchEntities(query),

		render: () => {
			let component;
			let popup;

			return {
				onStart: (props) => {
					component = new ReactRenderer(EntityMentionList, {
						props,
						editor: props.editor,
					});

					if (!props.clientRect) return;

					popup = tippy('body', {
						getReferenceClientRect: props.clientRect,
						appendTo: () => document.body,
						content: component.element,
						showOnCreate: true,
						interactive: true,
						trigger: 'manual',
						placement: 'bottom-start',
					});
				},

				onUpdate(props) {
					component.updateProps(props);
					if (!props.clientRect) return;
					popup[0].setProps({ getReferenceClientRect: props.clientRect });
				},

				onKeyDown(props) {
					if (props.event.key === 'Escape') {
						popup[0].hide();
						return true;
					}
					return component.ref?.onKeyDown(props);
				},

				onExit() {
					popup[0]?.destroy();
					component?.destroy();
				},
			};
		},
	},
});

export default EntityMention;
